import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Search,
  Flame,
  Sparkles,
  Crosshair,
  Swords,
  Crown,
  TrendingUp,
  Compass,
  LayoutGrid,
  ListChecks,
  CalendarHeart,
  Clock,
  DollarSign,
  AlertCircle,
  KeyRound,
} from 'lucide-react';
import { Card, CardTitle, CardDescription } from '@/components/ui';

/**
 * 사용법 가이드 — 기능별 상세 안내 + 오류 대응
 */
export function Help() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <BookOpen className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">사용법 가이드</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          각 기능을 클릭하면 펼쳐집니다. 오류 메시지가 뜨면 맨 아래 "오류 대응" 섹션도 확인해주세요.
        </p>
      </header>

      <Card className="border-gold/30 bg-gradient-to-br from-gold/5 to-transparent">
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-gold" /> 사용 시작 전 — 본인 API 키 준비
        </CardTitle>
        <CardDescription>
          블로그 코치 · 1page 분석 · 경쟁 격차는 사용자 본인의 <strong>Google Gemini API 키</strong>가 필요합니다.
        </CardDescription>
        <ol className="mt-3 space-y-1.5 text-sm text-gray-300 list-decimal list-inside">
          <li>
            <a className="text-gold hover:underline" href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">
              aistudio.google.com/apikey
            </a>
            {' '}접속 → Google 로그인 → "Create API key" 클릭 (무료, 일 1,500회)
          </li>
          <li>발급된 키 (AIzaSy... 형식) 복사</li>
          <li>
            <Link to="/settings" className="text-gold hover:underline">/settings</Link> 에 키 + 본인 블로그 URL 저장 → 매번 입력 안 해도 됨
          </li>
          <li>이 키는 우리 서버에 절대 저장되지 않음 (브라우저 localStorage 만). 호출 시 body 로 전달되어 Gemini 로 바로 감.</li>
        </ol>
      </Card>

      <FeatureSection
        icon={<Search className="h-5 w-5 text-gold" />}
        title="키워드 분석"
        path="/analyze"
        cost="우리 키 풀 사용 (사용자 부담 0)"
      >
        <p>
          키워드를 입력하면 GodScore · 포화도 · 꿀통 · 기회점수 · 60일 트렌드 · 영역별 분포 · 발행 최적 요일 · 수익화
          잠재력까지 한번에 볼 수 있어요.
        </p>
        <h4 className="mt-3 font-bold text-white">읽는 법</h4>
        <ul className="mt-1 space-y-1 list-disc list-inside text-sm">
          <li><strong className="text-gold">GodScore</strong> (0~100): 종합점수. 포화25 + 꿀통30 + 기회20 + 검색량25</li>
          <li><strong>포화도</strong>: 낮을수록 좋음 (블로그 글 수 ÷ 월검색). 30 미만이 황금권</li>
          <li><strong>꿀통</strong>: 클릭률·경쟁도·트렌드 종합. 높을수록 광고/조회수 효율 ↑</li>
          <li><strong>등급</strong>: S(85+) / A(75+) / B(65+) / C(50+) / D</li>
          <li><strong>대안 키워드</strong>: 우측에 "더 점수 높은 5개" 자동 추천</li>
        </ul>
      </FeatureSection>

      <FeatureSection
        icon={<Flame className="h-5 w-5 text-orange-400" />}
        title="내일 핫해질 키워드"
        path="/trending"
        cost="우리 키 풀 사용"
      >
        <p>최근 7일 평균 검색량이 이전 7일 평균보다 가파르게 상승한 키워드 TOP 15.</p>
        <h4 className="mt-3 font-bold text-white">활용</h4>
        <ul className="mt-1 space-y-1 list-disc list-inside text-sm">
          <li>리스트의 키워드 클릭 → 바로 분석 페이지로 이동</li>
          <li>가속도 +30% 이상이면 글쓰기 시점에 즉시 노출 직전. 24~48시간 안에 발행 권장</li>
          <li>한계: 이미 분석된 키워드 풀 내에서만 발견. 완전 신규 유행어는 "키워드 발굴" 메뉴 병행</li>
          <li>6시간마다 자동 갱신. 첫 호출만 30~60초 (DataLab 80개 호출 + 캐싱)</li>
        </ul>
      </FeatureSection>

      <FeatureSection
        icon={<Sparkles className="h-5 w-5 text-gold" />}
        title="블로그 코치"
        path="/blog-coach"
        cost="사용자 Gemini 키 + 우리 키 풀"
      >
        <p>본인 블로그 URL + Gemini 키 입력 → 최근 20개 글 RSS 분석 → 정체성 진단 + 시즌까지 고려한 키워드 30개 추천.</p>
        <h4 className="mt-3 font-bold text-white">읽는 법</h4>
        <ul className="mt-1 space-y-1 list-disc list-inside text-sm">
          <li><strong>정체성 카드</strong>: AI가 파악한 내 블로그 topic / audience / style</li>
          <li><strong>다가오는 시즌</strong>: 90일 내 시즌 이벤트 자동 매칭 (예: 빼빼로데이 D-15)</li>
          <li><strong>키워드 30개</strong>: 점수 + 등급 + 인덱스 여부. 행 클릭 시 즉시 분석</li>
          <li>2026-01-01 이전 글은 분석에서 제외 (최신 트렌드 우선)</li>
          <li>키워드 30개 일괄 복사 버튼 → 캘린더/노션에 붙여넣기</li>
        </ul>
      </FeatureSection>

      <FeatureSection
        icon={<Crosshair className="h-5 w-5 text-gold" />}
        title="1page 점유 분석"
        path="/serp-analysis"
        cost="사용자 Gemini 키 + 우리 키 풀"
      >
        <p>키워드 + Gemini 키 입력 → 네이버 1page 상위 30개 (블로그/카페/뉴스) → AI가 진단.</p>
        <h4 className="mt-3 font-bold text-white">결과 카드</h4>
        <ul className="mt-1 space-y-1 list-disc list-inside text-sm">
          <li><strong>공통 주제</strong>: 모두가 다루는 흔한 각도 (피해야 할 클리셰)</li>
          <li><strong>빈자리 (missing angles)</strong>: 아무도 안 다룬 진입 포인트 — <strong>여기가 1page 진입 기회</strong></li>
          <li><strong>1위 후보 제목 5개</strong>: AI가 작성한 클릭 유도 제목</li>
          <li><strong>롱테일 키워드</strong>: 함께 노려볼 만한 세부 검색어</li>
        </ul>
      </FeatureSection>

      <FeatureSection
        icon={<Swords className="h-5 w-5 text-gold" />}
        title="경쟁 격차 분석"
        path="/blog-compare"
        cost="사용자 Gemini 키"
      >
        <p>내 블로그 + 라이벌 1~3개 URL 입력 → AI가 비교 → "라이벌이 다루는데 내가 안 다룬" Gap 키워드 추출.</p>
        <h4 className="mt-3 font-bold text-white">결과</h4>
        <ul className="mt-1 space-y-1 list-disc list-inside text-sm">
          <li><strong>내 정체성</strong> + 강점/약점 진단</li>
          <li><strong>라이벌 프로파일</strong>: 각 라이벌의 정체성과 강점</li>
          <li><strong>Gap 키워드</strong>: 우선 공략할 주제 (출처 라이벌 표시)</li>
          <li><strong>액션 플랜</strong>: 구체적 실행 항목 5개</li>
        </ul>
      </FeatureSection>

      <FeatureSection
        icon={<Crown className="h-5 w-5 text-gold" />}
        title="황금키워드"
        path="/golden"
        cost="공개 (캐시)"
      >
        <p>GodScore ≥ 65, 포화도 &lt; 30, 월검색 ≥ 500 조건의 키워드 풀 100개. 24h KV 캐시.</p>
        <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
          <li>필터: 키워드명/GodScore/포화도/검색량</li>
          <li>정렬: 4개 키 × 2방향</li>
          <li><strong>CSV 다운로드</strong>: 필터 무관 전체 100개 + 20개 컬럼</li>
        </ul>
      </FeatureSection>

      <FeatureSection
        icon={<TrendingUp className="h-5 w-5 text-gold" />}
        title="순위 추적"
        path="/rank"
        cost="우리 키 풀"
      >
        <p>키워드 + 내 블로그 글 URL 등록 → 2분마다 자동으로 네이버 view 영역 순위 체크.</p>
        <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
          <li>최근 24시간 안 본 대상은 즉시 갱신, 그 외는 stale</li>
          <li>30일 이력 그래프 제공</li>
        </ul>
      </FeatureSection>

      <FeatureSection
        icon={<Compass className="h-5 w-5 text-gold" />}
        title="키워드 발굴"
        path="/discover"
        cost="우리 키 풀"
      >
        <p>시드 키워드 → 자동완성 + 연관 키워드 + 트렌딩 칩 한꺼번에. 새 도메인 탐색에 유용.</p>
      </FeatureSection>

      <FeatureSection
        icon={<LayoutGrid className="h-5 w-5 text-gold" />}
        title="카테고리"
        path="/categories"
        cost="공개"
      >
        <p>18개 도메인 카탈로그 (요리/뷰티/IT/육아 등). 카테고리별 키워드 풀 + 최소 검색량 필터.</p>
      </FeatureSection>

      <FeatureSection
        icon={<ListChecks className="h-5 w-5 text-gold" />}
        title="대량 분석"
        path="/bulk"
        cost="우리 키 풀"
      >
        <p>키워드를 여러 개 한번에 붙여넣어 일괄 분석. 결과는 CSV로 받을 수 있어요.</p>
      </FeatureSection>

      <FeatureSection
        icon={<CalendarHeart className="h-5 w-5 text-gold" />}
        title="시즌 키워드"
        path="/season"
        cost="공개"
      >
        <p>D-60 이내 다가오는 시즌 이벤트 (수능, 명절, 빼빼로데이 등). 종료된 이벤트는 자동 제외.</p>
      </FeatureSection>

      <FeatureSection
        icon={<Clock className="h-5 w-5 text-violet-300" />}
        title="발행 최적 요일 (분석 페이지 안에)"
        path="/analyze"
        cost="우리 키 풀"
      >
        <p>키워드 분석 페이지 안에 자동으로 표시. 60일 일자별 검색량을 요일별로 집계해 피크 요일 1일 전 저녁 발행을 추천.</p>
      </FeatureSection>

      <FeatureSection
        icon={<DollarSign className="h-5 w-5 text-emerald-300" />}
        title="수익화 잠재력 (분석 페이지 안에)"
        path="/analyze"
        cost="우리 키 풀"
      >
        <p>월 클릭수 × 추정 CPC × 점유율 시나리오 3종 (상위 30% / 중위 10% / 하위 3%).</p>
        <p className="mt-2 text-xs text-gray-500">
          ※ 추정 CPC: 경쟁도별(낮음 50원 / 중간 150원 / 높음 400원). 실제는 광고주/시즌에 따라 ±2배 변동.
        </p>
      </FeatureSection>

      <ErrorSection />
    </div>
  );
}

// ─── 펼치는 섹션 ──────────────────────────────────────────

function FeatureSection({
  icon,
  title,
  path,
  cost,
  children,
  defaultOpen = false,
}: {
  icon: React.ReactNode;
  title: string;
  path: string;
  cost: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
        )}
        {icon}
        <span className="text-base font-bold text-white flex-1">{title}</span>
        <Link
          to={path}
          onClick={(e) => e.stopPropagation()}
          className="hidden sm:inline-flex text-[11px] text-gold hover:underline"
        >
          {path} →
        </Link>
        <span className="hidden md:inline-block text-[10px] rounded-full bg-gray-800 px-2 py-0.5 text-gray-400">
          {cost}
        </span>
      </button>
      {open && <div className="mt-4 text-sm text-gray-300 space-y-2">{children}</div>}
    </Card>
  );
}

// ─── 오류 대응 ──────────────────────────────────────────

function ErrorSection() {
  const errors: Array<{ msg: string; cause: string; fix: string }> = [
    {
      msg: 'Gemini 응답이 올바른 JSON이 아닙니다',
      cause: 'AI가 가끔 형식 깨진 응답을 줍니다. 또는 키 쿼터 초과 직전 truncate.',
      fix: '한 번 더 시도해보세요. 반복되면 (1) 키워드/URL 단순화 (2) 다른 시간에 재시도 (3) Google AI Studio 에서 quota 확인',
    },
    {
      msg: 'NaverAdsApiError / 광고 API에 없는 키워드입니다',
      cause: '네이버 광고 데이터에 등록 안 된 신조어/긴 자연 검색어.',
      fix: '자동 변형(공백 제거 등)이 시도됩니다. 그래도 실패하면 더 짧은 핵심어로 분석 후 응용해보세요.',
    },
    {
      msg: '서버 응답이 비정상입니다 (502 / 504)',
      cause: '네이버 API 일시 장애 또는 우리 키 풀 전부 rate-limit.',
      fix: '30초~1분 뒤 재시도. 반복되면 다음 6시간 캐시 갱신을 기다리는 게 빠릅니다.',
    },
    {
      msg: 'RSS 파싱 실패 / 블로그 글이 없습니다',
      cause: '비공개 블로그, 잘못된 URL, 게시글 0개, 네이버 외 플랫폼.',
      fix: 'blog.naver.com/사용자ID 형식 확인. RSS 비공개 설정이면 블로그 관리 → RSS 발행 활성화 필요.',
    },
    {
      msg: '인증이 필요합니다 (401)',
      cause: '로그인 세션 만료 (보통 30일).',
      fix: '우측 상단 로그아웃 후 다시 Google 로그인.',
    },
    {
      msg: '승인 대기 (pending)',
      cause: '신규 가입 시 운영진 승인 필요.',
      fix: '닉네임 입력해서 가입했는지 확인. 챌린지 운영팀에 문의.',
    },
    {
      msg: '핫 키워드 분석 실패 / 데이터 부족',
      cause: '분석된 키워드 풀 부족 또는 가속도 조건 미충족.',
      fix: '키워드 풀이 더 쌓일 때까지 기다리거나 "키워드 발굴" 로 새 시드 투입.',
    },
    {
      msg: '발행 최적 요일 카드가 안 보임',
      cause: 'DataLab 응답이 14일 미만 (신조어).',
      fix: '검색량이 어느 정도 누적된 키워드만 표시됩니다. 더 일반적 동의어로 재분석.',
    },
    {
      msg: '트렌드 배지가 회색 (정체)',
      cause: '60일 변화율 절댓값 5% 미만.',
      fix: '안정 키워드. 굳이 트렌드 보고 결정할 필요 없는 evergreen 주제.',
    },
  ];

  return (
    <Card className="border-red-500/30 bg-red-500/5">
      <CardTitle className="flex items-center gap-2 text-red-200">
        <AlertCircle className="h-5 w-5" /> 오류 대응 가이드
      </CardTitle>
      <CardDescription>자주 보는 오류와 해결 방법</CardDescription>
      <div className="mt-4 space-y-3">
        {errors.map((e, i) => (
          <div key={i} className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
            <div className="text-sm font-bold text-red-200">⚠️ {e.msg}</div>
            <div className="mt-1.5 text-xs text-gray-400">
              <strong className="text-gray-300">원인:</strong> {e.cause}
            </div>
            <div className="mt-1 text-xs text-emerald-200/80">
              <strong className="text-emerald-300">해결:</strong> {e.fix}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-lg border border-gray-800 bg-gray-950 p-3 text-xs text-gray-400">
        <strong className="text-white">그래도 안 되면?</strong> 챌린지 운영팀에 (1) 어떤 메뉴에서 (2) 어떤 키워드/URL로
        (3) 어떤 메시지가 떴는지 함께 알려주세요. 가능하면 시간(분 단위)까지.
      </div>
    </Card>
  );
}
